<template>
  <!-- Администратор видит факты изменений всего конфигуратора текущей кампании. -->
  <v-card class="admission-config-log" elevation="0">
    <v-card-title class="admission-config-log__header">
      <div>
        <h2 class="admission-config-log__title">Журнал действий</h2>
        <p class="admission-config-log__description">
          Изменения, внесённые пользователями в конфигуратор приёмной комиссии.
        </p>
      </div>

      <v-btn
        color="primary"
        variant="tonal"
        prepend-icon="mdi-refresh"
        :loading="loading"
        @click="loadLog()"
      >
        Обновить
      </v-btn>
    </v-card-title>

    <v-card-text class="admission-config-log__content">
      <v-alert
        v-if="error"
        class="mb-4"
        type="error"
        variant="tonal"
        closable
        @click:close="error = ''"
      >
        {{ error }}
      </v-alert>

      <v-data-table-server
        v-model:page="page"
        v-model:items-per-page="itemsPerPage"
        class="admission-config-log__table"
        :headers="headers"
        :items="items"
        :items-length="totalItems"
        :items-per-page-options="[15, 30, 50, 100]"
        :loading="loading"
        loading-text="Загрузка журнала..."
        no-data-text="Событий пока нет"
        @update:options="loadLog"
      >
        <template #item.action="{ item }">
          <v-chip
            :color="actionColor(item.action)"
            variant="tonal"
            size="small"
          >
            {{ actionName(item.action) }}
          </v-chip>
        </template>

        <template #item.description="{ item }">
          <span class="admission-config-log__event-description">
            {{ item.description || 'Изменение настроек приёмной комиссии' }}
          </span>
        </template>

        <template #item.object="{ item }">
          <span class="admission-config-log__object">
            {{ formatObject(item) }}
          </span>
        </template>
      </v-data-table-server>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'AdmissionConfigLogSection',
  data() {
    return {
      loading: false,
      error: '',
      items: [],
      totalItems: 0,
      page: 1,
      itemsPerPage: 30,
      abortController: null,
      headers: [
        { title: 'Дата', key: 'date', width: 155, sortable: false },
        { title: 'Пользователь', key: 'actor', width: 190, sortable: false },
        { title: 'Действие', key: 'action', width: 125, sortable: false },
        { title: 'Описание', key: 'description', sortable: false },
        { title: 'Объект', key: 'object', width: 210, sortable: false }
      ],
      objectNames: {
        psm_settings: 'Настройки',
        psm_admission_address: 'Адрес приёмной комиссии',
        psm_adm_calendar: 'Календарь',
        psm_adm_target_organizations: 'Организация целевого набора',
        psm_specialty_params: 'Конкурсная группа',
        psm_adm_received_documents: 'Документ',
        psm_adm_privilege_groups: 'Льгота',
        psm_adm_steps: 'Этап рейтинга',
        psm_admission_statistic: 'Приёмная кампания'
      }
    };
  },
  beforeUnmount() {
    this.abortRequest();
  },
  methods: {
    loadLog(options = {}) {
      // Меню скрывает раздел, но клиент также не отправляет запрос без
      // административного признака; окончательная проверка остаётся на сервере.
      if (!this.$model.isAdmin) {
        return;
      }

      const page = Number(options.page || this.page);
      const itemsPerPage = Number(options.itemsPerPage || this.itemsPerPage);
      this.page = page;
      this.itemsPerPage = itemsPerPage;
      this.abortRequest();

      const abortController = new AbortController();
      this.abortController = abortController;
      this.loading = true;
      this.error = '';

      this.axios
        .get(
          this.$model.wwwroot
            + '/procollege/manage_groups/admission/config/getAdmissionConfigLog',
          {
            params: {
              page,
              itemsPerPage
            },
            signal: abortController.signal
          }
        )
        .then((response) => {
          if (this.abortController !== abortController) {
            return;
          }

          const data = response.data || {};
          this.items = Array.isArray(data.items) ? data.items : [];
          this.totalItems = Number(data.total || 0);
        })
        .catch((error) => {
          if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
            return;
          }

          this.items = [];
          this.totalItems = 0;
          this.error = 'Не удалось загрузить журнал действий конфигуратора.';
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          if (this.abortController !== abortController) {
            return;
          }

          this.abortController = null;
          this.loading = false;
        });
    },
    abortRequest() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    },
    actionName(action) {
      return {
        create: 'Добавление',
        update: 'Изменение',
        delete: 'Удаление',
        close: 'Закрытие'
      }[action] || action || 'Событие';
    },
    actionColor(action) {
      return {
        create: 'success',
        update: 'primary',
        delete: 'error',
        close: 'warning'
      }[action] || 'secondary';
    },
    formatObject(item) {
      const name = this.objectNames[item.objectTable] || item.objectTable || 'Объект';
      return item.objectId ? `${name} #${item.objectId}` : name;
    }
  }
};
</script>

<style scoped>
.admission-config-log {
  border: 1px solid #e4e7ec;
  border-radius: 12px;
}

.admission-config-log__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 24px;
  white-space: normal;
}

.admission-config-log__title {
  margin: 0;
  color: #1d2939;
  font-size: 22px;
  font-weight: 600;
  line-height: 1.3;
}

.admission-config-log__description {
  margin: 6px 0 0;
  color: #667085;
  font-size: 14px;
  font-weight: 400;
}

.admission-config-log__content {
  padding: 0 24px 24px;
}

.admission-config-log__table {
  border: 1px solid #e4e7ec;
  border-radius: 9px;
}

.admission-config-log__event-description {
  display: inline-block;
  min-width: 260px;
  padding: 8px 0;
  color: #344054;
  white-space: normal;
}

.admission-config-log__object {
  color: #667085;
  font-size: 13px;
  white-space: normal;
}

@media (max-width: 700px) {
  .admission-config-log__header {
    flex-direction: column;
  }
}
</style>
